import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { 
  FaTrash, 
  FaEdit, 
  FaPlus, 
  FaTrophy, 
  FaUsers,
  FaMoneyBillWave,
  FaDoorOpen,
  FaKey,
  FaSpinner,
  FaTimesCircle
} from 'react-icons/fa';
import CreateMatchForm from './CreateMatchForm';
import EditMatchForm from './EditMatchForm';
import './AdminMatchList.css';

const AdminMatchList = () => {
  const [matches, setMatches] = useState([]);
  const [editingMatch, setEditingMatch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('create');

  const fetchMatches = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await axios.get('https://backend.aubattle.com/api/matches');
      setMatches(res.data);
    } catch (err) {
      console.error("Failed to fetch matches", err);
      setError("Failed to load matches. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMatches();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this match?")) return;
    try {
      await axios.delete(`https://backend.aubattle.com/api/matches/${id}`);
      setMatches(matches.filter(match => match.id !== id));
    } catch (err) {
      console.error("Delete failed", err);
      setError("Failed to delete match. Please try again.");
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'edit' && editingMatch === null) {
      setActiveTab('create');
    }
  };

  return (
    <div className="admin-match-container">
      <div className="admin-header">
        <h1><FaTrophy /> Match Management</h1>
        <p>Create, edit and manage tournament matches</p>
      </div>

      {error && (
        <div className="alert error">
          <FaTimesCircle /> {error}
        </div>
      )}

      <div className="match-form-container">
        <div className="form-tabs">
          <button
            className={`tab-btn ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => handleTabChange('create')}
          >
            <FaPlus /> Create New Match
          </button>
          <button
            className={`tab-btn ${activeTab === 'edit' ? 'active' : ''}`}
            onClick={() => handleTabChange('edit')}
            disabled={!editingMatch}
          >
            <FaEdit /> Edit Match
          </button>
        </div>

        <div className="form-content">
          {activeTab === 'create' ? (
            <CreateMatchForm onCreated={fetchMatches} />
          ) : (
            <EditMatchForm 
              match={editingMatch} 
              onCancel={() => {
                setEditingMatch(null);
                setActiveTab('create');
              }} 
              onUpdated={() => {
                fetchMatches();
                setActiveTab('create');
              }} 
            />
          )}
        </div>
      </div>

      <div className="matches-section">
        <div className="section-header">
          <h2>All Matches</h2>
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-value">{matches.length}</span>
              <span className="stat-label">Total Matches</span>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="loading-state">
            <FaSpinner className="spin" /> Loading matches...
          </div>
        ) : matches.length === 0 ? (
          <div className="empty-state">
            No matches found. Create one to get started.
          </div>
        ) : (
          <div className="matches-table-container">
            <table className="matches-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th><FaTrophy /> Prize</th>
                  <th><FaMoneyBillWave /> Entry</th>
                  <th><FaUsers /> Players</th>
                  <th><FaDoorOpen /> Room ID</th>
                  <th><FaKey /> Password</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((match) => (
                  <tr key={match.id}>
                    <td className="match-title">
                      <strong>{match.title}</strong>
                    </td>
                    <td className="text-center">
                      ${match.prize}
                    </td>
                    <td className="text-center">
                      ${match.entry_fee}
                    </td>
                    <td className="text-center">
                      {match.current_players || 0}/{match.max_players}
                    </td>
                    <td className="text-center">
                      {match.room_id || '-'}
                    </td>
                    <td className="text-center">
                      {match.room_password ? '••••' : '-'}
                    </td>
                    <td className="actions-cell">
                      <button 
                        className="edit-btn"
                        onClick={() => {
                          setEditingMatch(match);
                          setActiveTab('edit');
                        }}
                      >
                        <FaEdit /> Edit
                      </button>
                      <button 
                        className="delete-btn"
                        onClick={() => handleDelete(match.id)}
                      >
                        <FaTrash /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMatchList;